import type { ReactNode, RefObject } from "react";
import { styled } from "@pigment-css/react";
import {
  AnimateHeight,
  type AnimateHeightProps,
} from "@acto/taskpane/field-sales-2/components/primitives/AnimateHeight";
import { PinnedCaption } from "@acto/taskpane/field-sales-2/components/primitives/PinnedCaption";
import { Card } from "./Card";
import { RowListEmpty } from "./rows";
import { MetaLabel } from "./sections";

const List = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[12],
}));

const RowListEmptyFloor = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: `${theme.fieldSalesTwo.spacing[32]} 0`,
}));

export function RowListEmptyState({ children }: { children: ReactNode }) {
  return (
    <RowListEmptyFloor>
      <RowListEmpty>{children}</RowListEmpty>
    </RowListEmptyFloor>
  );
}

// Re-adds the row hover-bleed inset (HoverRowItem's negative inline margin) that the generic
// AnimateHeight's overflow:hidden would otherwise clip on the horizontal axis.
const BleedingAnimateHeight = styled(AnimateHeight)(({ theme }) => ({
  marginInline: `-${theme.fieldSalesTwo.spacing[12]}`,
  paddingInline: theme.fieldSalesTwo.spacing[12],
}));

const PositionedCard = styled(Card)({ position: "relative" });

const CardCaption = styled(PinnedCaption)(({ theme }) => ({
  marginInline: `-${theme.fieldSalesTwo.spacing[20]}`,
  // The pair must match `Card`'s gap: the opaque block then ends exactly where the first row starts.
  marginBottom: `-${theme.fieldSalesTwo.spacing[24]}`,
  paddingInline: theme.fieldSalesTwo.spacing[20],
  paddingBottom: theme.fieldSalesTwo.spacing[24],
  // Always opaque in the card's own flat tone, so the rows never show through while the pin fades in.
  background: theme.fieldSalesTwo.colors.surface.glassLightFlat,
  // Both layers overhang the card's 1px border inline, so its side edges end under the caption.
  "&::before, &::after": { insetInline: `-${theme.strokeWidth.thin}` },
}));

export interface RowListCardProps {
  transitionKey: AnimateHeightProps["transitionKey"];
  header?: ReactNode;
  caption?: ReactNode;
  /** The scrollport the caption pins under. Without it the caption is a plain label that scrolls away. */
  captionScrollRef?: RefObject<HTMLDivElement | null>;
  nested?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}

export function RowListCard({
  transitionKey,
  header,
  caption,
  captionScrollRef,
  nested,
  footer,
  children,
}: RowListCardProps) {
  return (
    <List>
      {header}
      <PositionedCard nested={nested}>
        {caption &&
          (captionScrollRef ? (
            <CardCaption scrollRef={captionScrollRef}>
              <MetaLabel>{caption}</MetaLabel>
            </CardCaption>
          ) : (
            <MetaLabel>{caption}</MetaLabel>
          ))}
        <BleedingAnimateHeight transitionKey={transitionKey}>{children}</BleedingAnimateHeight>
      </PositionedCard>
      {footer}
    </List>
  );
}
